import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Link } from '@material-ui/core';

import DeliveryFone from 'assets/images/deliveryFone.png';
import Delivery_1 from 'assets/images/delivery_1.png';
import Delivery_2 from 'assets/images/delivery_2.png';
import Delivery_3 from 'assets/images/delivery_3.png';
import Delivery_4 from 'assets/images/delivery_4.png';
import { ReactComponent as YoutubeIcon } from 'assets/icons/main-24/youtube--24.svg';
import { ReactComponent as LinkIcon } from 'assets/icons/status/check-circle--24.svg';

import { Text, TextTypography } from 'components/Text/Text';
import { Header } from 'components/Header/Header';
import { FooterInternalPages } from 'components/Footer/FooterInternalPages';
import { Icon } from 'components/Icon/Icon';
import useScrollPosition from '@react-hook/window-scroll';

import i18n from '../../../i18n';
import useVideoLink from '../../core/hooks/useVideoLink';

const useStyles = makeStyles(theme => ({
    mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    headerContainer: {
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        zIndex: 1,
        backgroundColor: (props: { scrollY: number }) => (props.scrollY > 664 ? '#FBFEF9' : null),
    },
    tagline: {
        height: 664,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        background: `url(${DeliveryFone}) no-repeat`,
        backgroundSize: 'cover',
        backgroundPosition: '60%',
    },
    taglineLink: {
        display: 'flex',
        alignContent: 'center',
        marginTop: theme.spacing(5),
        marginLeft: theme.spacing(17.75),
        height: 24,
        [theme.breakpoints.down(1280)]: {
            marginLeft: theme.spacing(5),
        },
        [theme.breakpoints.down(768)]: {
            marginLeft: theme.spacing(4),
        },
        [theme.breakpoints.down(480)]: {
            marginLeft: theme.spacing(2),
        },
    },
    taglineLinkText: {
        marginLeft: theme.spacing(1),
        color: '#FFFFFF',
        fontSize: 16,
        lineHeight: 1.5,
        [theme.breakpoints.down(321)]: {
            fontSize: 14,
        },
    },
    additionalLinkText: {
        color: '#5DCEFF',
    },
    taglineText: {
        maxWidth: 537,
        marginLeft: theme.spacing(17.75),
        color: '#FFFFFF',
        textShadow: '0px 0px 60px rgba(0, 0, 0, 0.8)',
        [theme.breakpoints.down(1280)]: {
            marginLeft: theme.spacing(5),
        },
        [theme.breakpoints.down(768)]: {
            marginLeft: theme.spacing(4),
        },
        [theme.breakpoints.down(480)]: {
            marginLeft: theme.spacing(2),
        },
    },
    additionalContainer: {
        width: '100%',
        padding: theme.spacing(10, 17.75),
        margin: '0 auto',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        [theme.breakpoints.down(1280)]: {
            padding: theme.spacing(10, 5),
        },
        [theme.breakpoints.down(960)]: {
            padding: theme.spacing(8, 5),
        },
        [theme.breakpoints.down(768)]: {
            padding: theme.spacing(6, 4),
        },
        [theme.breakpoints.down(480)]: {
            padding: theme.spacing(6, 2),
        },
    },
    additionalTitle: {
        width: '100%',
        marginTop: theme.spacing(7),
        '&:first-child': {
            marginTop: 0,
        },
        [theme.breakpoints.down(960)]: {
            marginTop: theme.spacing(8),
        },
        [theme.breakpoints.down(768)]: {
            marginTop: theme.spacing(6),
        },
    },
    additionalTitleText: {
        letterSpacing: -1,
        [theme.breakpoints.down(321)]: {
            fontSize: 24,
            lineHeight: 1.33,
        },
    },
    additionalSubTitle: {
        width: 'inherit',
        textAlign: 'left',
        marginTop: theme.spacing(3),
    },
    gridSmall: {
        margin: theme.spacing(6, 0),
        borderRadius: theme.spacing(1),
        [theme.breakpoints.down(768)]: {
            margin: theme.spacing(4, 0),
        },
    },
    gridBig: {
        width: '100%',
        margin: theme.spacing(6, 0),
        overflow: 'hidden',
        border: '1px solid rgba(36, 49, 67, 0.2)',
        boxShadow: '0px 15px 32px -3px rgba(36, 49, 67, 0.2)',
        boxSizing: 'border-box',
        borderRadius: theme.spacing(1),
        [theme.breakpoints.down(768)]: {
            margin: theme.spacing(4, 0),
        },
    },
    additionalImg: {
        objectFit: 'fill',
        width: '100%',
        height: '100%',
        borderRadius: theme.spacing(1),
    },
    footerContainer: {
        width: '100%',
        margin: '0 auto',
    },
    link: {
        maxHeight: theme.spacing(3),
        display: 'flex',
        alignItem: 'center',
        textDecoration: 'none',
    },
    paragraph: {
        display: 'flex',
        marginBottom: theme.spacing(2),
        '&:last-child': {
            marginBottom: 0,
        },
        '& svg': {
            marginRight: theme.spacing(2),
        },
    },
}));

export const Delivery = () => {
    const scrollY = useScrollPosition(60 /*fps*/);
    const classes = useStyles({ scrollY });
    const { t } = i18n.useTranslation();
    const videoLink = useVideoLink();

    return (
        <div className={classes.mainContainer}>
            <div className={classes.headerContainer}>
                <Header />
            </div>

            <div className={classes.tagline}>
                <Text className={classes.taglineText} typography={TextTypography.TITLE_STRONG_CHANGE}>
                    {t('pages:delivery.title')}
                </Text>
                <div className={classes.taglineLink}>
                    <Icon component={YoutubeIcon} />
                    <Link className={classes.link} href={videoLink} target="_blank" rel="noreferrer" color="inherit">
                        <Text className={classes.taglineLinkText} typography={TextTypography.TEXT_ACCENT_MEDIUM_CHANGE}>
                            {t('pages:delivery.subtitle')}
                        </Text>
                    </Link>
                </div>
            </div>
            <div className={classes.additionalContainer}>
                <div className={classes.additionalTitle}>
                    <Text className={classes.additionalTitleText} typography={TextTypography.HUGE_TITLE_BOLD_CHANGE}>
                        {t('pages:delivery.block1.title')}
                    </Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text className={classes.additionalTitleText} typography={TextTypography.TITLE_ACCENT_CHANGE}>
                        {t('pages:delivery.block1.subTitle')}
                    </Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:delivery.block1.text')}</Text>
                </div>
                <Grid container>
                    <Grid item xs className={classes.gridSmall}>
                        <img className={classes.additionalImg} src={Delivery_1} alt={t('common:photoAltMan')} />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:delivery.block1.text1')}</Text>
                </div>
                <div className={classes.additionalTitle}>
                    <Text typography={TextTypography.TITLE_ACCENT_CHANGE}>{t('pages:delivery.block1.text2')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:delivery.block1.text3')}</Text>
                </div>

                <div className={classes.additionalSubTitle}>
                    <div className={classes.paragraph}>
                        <Icon component={LinkIcon} />
                        <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                            {t('pages:delivery.block1.text4')}
                        </Text>
                    </div>
                    <div className={classes.paragraph}>
                        <Icon component={LinkIcon} />
                        <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                            {t('pages:delivery.block1.text5')}
                        </Text>
                    </div>
                    <div className={classes.paragraph}>
                        <Icon component={LinkIcon} />
                        <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                            {t('pages:delivery.block1.text6')}
                        </Text>
                    </div>
                    <div className={classes.paragraph}>
                        <Icon component={LinkIcon} />
                        <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                            {t('pages:delivery.block1.text7')}
                        </Text>
                    </div>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_ACCENT_MEDIUM_CHANGE}>
                        {t('pages:delivery.block1.text8')}
                    </Text>
                </div>
                <div className={classes.additionalTitle}>
                    <Text typography={TextTypography.TITLE_ACCENT_CHANGE}>{t('pages:delivery.block2.title')}</Text>
                </div>
                <div className={classes.additionalSubTitle}>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>{t('pages:delivery.block2.text')}</Text>
                </div>

                <Grid container>
                    <Grid item xs className={classes.gridBig}>
                        <img className={classes.additionalImg} src={Delivery_2} alt="форма регистрации" />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                        {t('pages:delivery.block1.text9')}
                        <span className={classes.additionalLinkText}>{t('pages:delivery.block1.text10')}</span>
                    </Text>
                </div>
                <Grid container>
                    <Grid item xs className={classes.gridBig}>
                        <img className={classes.additionalImg} src={Delivery_3} alt="логотипы служб доставки" />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                        {t('pages:delivery.block1.text11')}
                    </Text>
                </div>
                <Grid container>
                    <Grid item xs className={classes.gridBig}>
                        <img className={classes.additionalImg} src={Delivery_4} alt="сервисы" />
                    </Grid>
                </Grid>
                <div>
                    <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                        {t('pages:delivery.block1.text12')}
                    </Text>
                    <div>
                        <Text typography={TextTypography.TEXT_BODY_REGULAR_CHANGE}>
                            {t('pages:delivery.block1.text13')}
                        </Text>
                    </div>
                </div>
            </div>
            <div className={classes.footerContainer}>
                <FooterInternalPages />
            </div>
        </div>
    );
};
